import { useState } from 'react';

import { Frame } from '../../atoms/Frame';
import { Image } from '../../atoms/Image';
import type { TestIdProps } from '../../testing';
import styles from './ProductGallery.module.css';

export interface ProductGalleryImage {
  readonly src: string;
  readonly alt?: string;
}

export interface ProductGalleryProps extends TestIdProps {
  images: readonly ProductGalleryImage[];
  productName: string;
  className?: string | undefined;
}

export function ProductGallery({
  images,
  productName,
  className,
  'data-testid': testId = 'product-gallery',
}: ProductGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const classes = [styles['root'], className].filter(Boolean).join(' ');

  if (images.length === 0) return null;

  const safeIndex = Math.min(activeIndex, images.length - 1);
  const active = images[safeIndex];
  if (active === undefined) return null;
  const activeAlt = active.alt ?? productName;

  return (
    <div className={classes} data-testid={testId}>
      <Frame variant="image" padding="sm">
        <div className={styles['mainWrap']} data-testid={`${testId}-main`}>
          <Image
            src={active.src}
            alt={activeAlt}
            sizes="(max-width: 900px) 90vw, 50vw"
            priority
            className={styles['mainImage']}
            data-testid={`${testId}-image`}
          />
        </div>
      </Frame>
      {images.length > 1 && (
        <div
          className={styles['thumbs']}
          role="tablist"
          aria-label={`${productName} gallery thumbnails`}
        >
          {images.map((image, index) => {
            const selected = index === safeIndex;
            return (
              <button
                key={image.src}
                type="button"
                role="tab"
                aria-selected={selected}
                className={styles['thumb']}
                data-selected={selected ? 'true' : undefined}
                data-testid={`${testId}-thumb-${String(index)}`}
                onClick={() => {
                  setActiveIndex(index);
                }}
              >
                <Image src={image.src} alt="" sizes="72px" className={styles['thumbImage']} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
